var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _plugins, _getClient, _runHook, _normalizeRawArgument;
import { parse } from "valibot";
import { bcs } from "../bcs/index.js";
import { SUI_TYPE_ARG } from "../utils/index.js";
import { normalizeSuiAddress, normalizeSuiObjectId } from "../utils/sui-types.js";
import { ObjectRef } from "./blockData/v2.js";
import { Inputs, isMutableSharedObjectInput } from "./Inputs.js";
import {
  isTxContext,
  normalizedTypeToMoveTypeSignature,
  pureBcsSchemaFromOpenMoveTypeSignatureBody
} from "./serializer.js";
const MAX_OBJECTS_PER_FETCH = 50;
const GAS_SAFE_OVERHEAD = 1000n;
const chunk = (arr, size) => Array.from(
  { length: Math.ceil(arr.length / size) },
  (_, i) => arr.slice(i * size, i * size + size)
);
class DefaultTransactionBlockFeatures {
  constructor(plugins, getClient) {
    __privateAdd(this, _plugins, void 0);
    __privateAdd(this, _getClient, void 0);
    __privateAdd(this, _runHook, async (hook, ...args) => {
      for (const plugin of __privateGet(this, _plugins)) {
        if (plugin[hook]) {
          await plugin[hook](...args);
        }
      }
    });
    this.setGasPrice = async (blockData) => {
      await __privateGet(this, _runHook).call(this, "setGasPrice", blockData);
      if (blockData.gasConfig.price) {
        return;
      }
      blockData.gasConfig.price = String(await __privateGet(this, _getClient).call(this).getReferenceGasPrice());
    };
    this.setGasBudget = async (blockData, options) => {
      await __privateGet(this, _runHook).call(this, "setGasBudget", blockData, options);
      if (!blockData.gasConfig.budget) {
        const dryRunResult = await __privateGet(this, _getClient).call(this).dryRunTransactionBlock({
          transactionBlock: blockData.build({
            maxSizeBytes: options.maxTxSizeBytes,
            overrides: {
              gasData: {
                budget: String(options.maxTxGas),
                payment: []
              }
            }
          })
        });
        if (dryRunResult.effects.status.status !== "success") {
          throw new Error(
            `Dry run failed, could not automatically determine a budget: ${dryRunResult.effects.status.error}`,
            { cause: dryRunResult }
          );
        }
        const safeOverhead = GAS_SAFE_OVERHEAD * BigInt(blockData.gasConfig.price || 1n);
        const baseComputationCostWithOverhead = BigInt(dryRunResult.effects.gasUsed.computationCost) + safeOverhead;
        const gasBudget = baseComputationCostWithOverhead + BigInt(dryRunResult.effects.gasUsed.storageCost) - BigInt(dryRunResult.effects.gasUsed.storageRebate);
        blockData.gasConfig.budget = String(
          gasBudget > baseComputationCostWithOverhead ? gasBudget : baseComputationCostWithOverhead
        );
      }
    };
    // The current default is just picking _all_ coins we can which may not be ideal.
    this.setGasPayment = async (blockData, options) => {
      await __privateGet(this, _runHook).call(this, "setGasPayment", blockData, options);
      if (blockData.gasConfig.payment) {
        if (blockData.gasConfig.payment.length > options.maxGasObjects) {
          throw new Error(`Payment objects exceed maximum amount: ${options.maxGasObjects}`);
        }
      }
      if (blockData.gasConfig.payment) {
        return;
      }
      const gasOwner = blockData.gasConfig.owner ?? blockData.sender;
      const coins = await __privateGet(this, _getClient).call(this).getCoins({
        owner: gasOwner,
        coinType: SUI_TYPE_ARG
      });
      const paymentCoins = coins.data.filter((coin) => {
        const matchingInput = blockData.inputs.find((input) => {
          if (input.Object?.ImmOrOwnedObject) {
            return coin.coinObjectId === input.Object.ImmOrOwnedObject.objectId;
          }
          return false;
        });
        return !matchingInput;
      }).slice(0, options.maxGasObjects - 1).map((coin) => ({
        objectId: coin.coinObjectId,
        digest: coin.digest,
        version: coin.version
      }));
      if (!paymentCoins.length) {
        throw new Error("No valid gas coins found for the transaction.");
      }
      blockData.gasConfig.payment = paymentCoins.map((payment) => parse(ObjectRef, payment));
    };
    this.resolveObjectReferences = async (blockData) => {
      await __privateGet(this, _runHook).call(this, "resolveObjectReferences", blockData);
      const objectsToResolve = blockData.inputs.filter((input) => {
        return input.UnresolvedObject;
      });
      if (objectsToResolve.length) {
        const dedupedIds = [
          ...new Set(
            objectsToResolve.map((input) => normalizeSuiObjectId(input.UnresolvedObject.value))
          )
        ];
        const objectChunks = chunk(dedupedIds, MAX_OBJECTS_PER_FETCH);
        const objects = (await Promise.all(
          objectChunks.map(
            (chunk2) => __privateGet(this, _getClient).call(this).multiGetObjects({
              ids: chunk2,
              options: { showOwner: true }
            })
          )
        )).flat();
        let objectsById = new Map(
          dedupedIds.map((id, index) => {
            return [id, objects[index]];
          })
        );
        const invalidObjects = Array.from(objectsById).filter(([_, obj]) => obj.error).map(([id, _]) => id);
        if (invalidObjects.length) {
          throw new Error(`The following input objects are invalid: ${invalidObjects.join(", ")}`);
        }
        objectsToResolve.forEach((input) => {
          let updated;
          const id = normalizeSuiAddress(input.UnresolvedObject.value);
          const typeSignatures = input.UnresolvedObject.typeSignatures;
          const object = objectsById.get(id);
          const owner = object.data?.owner;
          const initialSharedVersion = owner && typeof owner === "object" && "Shared" in owner ? owner.Shared.initial_shared_version : void 0;
          const isMutable = typeSignatures.some((typeSignature) => {
            const isByValue = !typeSignature.ref;
            return isMutableSharedObjectInput(input) || isByValue || typeSignature.ref === "&mut";
          });
          const isReceiving = !initialSharedVersion && typeSignatures.some(isReceivingType);
          if (initialSharedVersion) {
            updated = Inputs.SharedObjectRef({
              objectId: id,
              initialSharedVersion,
              mutable: isMutable
            });
          } else if (isReceiving) {
            updated = Inputs.ReceivingRef(object.data);
          }
          blockData.inputs[blockData.inputs.indexOf(input)] = updated ?? Inputs.ObjectRef(object.data);
        });
      }
    };
    this.normalizeInputs = async (blockData) => {
      await __privateGet(this, _runHook).call(this, "normalizeInputs", blockData);
      const { inputs, transactions } = blockData;
      const moveModulesToResolve = [];
      transactions.forEach((transaction) => {
        if (transaction.MoveCall) {
          const inputs2 = transaction.MoveCall.arguments.map((arg) => {
            if (arg.$kind === "Input") {
              return blockData.inputs[arg.Input];
            }
            return null;
          });
          const needsResolution = inputs2.some(
            (input) => input && (input.RawValue || input.UnresolvedObject)
          );
          if (needsResolution) {
            moveModulesToResolve.push(transaction.MoveCall);
          }
        }
        switch (transaction.$kind) {
          case "SplitCoins":
            transaction.SplitCoins[1].forEach((amount) => {
              __privateGet(this, _normalizeRawArgument).call(this, amount, bcs.U64, blockData);
            });
            break;
          case "TransferObjects":
            __privateGet(this, _normalizeRawArgument).call(this, transaction.TransferObjects[1], bcs.Address, blockData);
            break;
        }
      });
      if (moveModulesToResolve.length) {
        await Promise.all(
          moveModulesToResolve.map(async (moveCall) => {
            const normalized = await __privateGet(this, _getClient).call(this).getNormalizedMoveFunction({
              package: moveCall.package,
              module: moveCall.module,
              function: moveCall.function
            });
            const hasTxContext = normalized.parameters.length > 0 && isTxContext(normalized.parameters.at(-1));
            const params = hasTxContext ? normalized.parameters.slice(0, normalized.parameters.length - 1) : normalized.parameters;
            if (params.length !== moveCall.arguments.length) {
              throw new Error("Incorrect number of arguments.");
            }
            params.forEach((param, i) => {
              const arg = moveCall.arguments[i];
              if (arg.$kind !== "Input")
                return;
              const input = inputs[arg.Input];
              if (!input.RawValue && !input.UnresolvedObject)
                return;
              const inputValue = input.RawValue?.value ?? input.UnresolvedObject?.value;
              const typeSignature = normalizedTypeToMoveTypeSignature(param);
              if (typeof typeSignature.body !== "object" || "vector" in typeSignature.body) {
                const schema = pureBcsSchemaFromOpenMoveTypeSignatureBody(typeSignature.body);
                inputs[inputs.indexOf(input)] = Inputs.Pure(schema.serialize(inputValue));
                return;
              }
              if (typeof inputValue !== "string") {
                throw new Error(
                  `Expect the argument to be an object id string, got ${JSON.stringify(
                    inputValue,
                    null,
                    2
                  )}`
                );
              }
              if (input.$kind === "RawValue") {
                inputs[inputs.indexOf(input)] = {
                  $kind: "UnresolvedObject",
                  UnresolvedObject: {
                    value: inputValue,
                    typeSignatures: [typeSignature]
                  }
                };
              } else {
                input.UnresolvedObject.typeSignatures.push(typeSignature);
              }
            });
          })
        );
      }
    };
    this.validate = async (blockData, options) => {
      await __privateGet(this, _runHook).call(this, "validate", blockData, options);
      blockData.inputs.forEach((input, index) => {
        if (input.Pure) {
          if (input.Pure.length > options.maxPureArgumentSize) {
            throw new Error(
              `Input at index ${index} is too large, max pure input size is ${options.maxPureArgumentSize} bytes, got ${input.Pure.length} bytes`
            );
          }
        }
      });
    };
    __privateAdd(this, _normalizeRawArgument, (arg, schema, blockData) => {
      if (arg.$kind !== "Input") {
        return;
      }
      const input = blockData.inputs[arg.Input];
      if (input.$kind !== "RawValue") {
        return;
      }
      blockData.inputs[arg.Input] = Inputs.Pure(schema.serialize(input.RawValue.value));
    });
    __privateSet(this, _plugins, plugins);
    __privateSet(this, _getClient, getClient);
  }
}
_plugins = new WeakMap();
_getClient = new WeakMap();
_runHook = new WeakMap();
_normalizeRawArgument = new WeakMap();
function isReceivingType(type) {
  if (typeof type.body !== "object" || !("datatype" in type.body)) {
    return false;
  }
  return type.body.datatype.package === "0x2" && type.body.datatype.module === "transfer" && type.body.datatype.type === "Receiving";
}
export {
  DefaultTransactionBlockFeatures
};
//# sourceMappingURL=TransactionBlockPlugin.js.map
